import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { RotateCcw, ChevronLeft, RefreshCw } from 'lucide-react';
import { api } from '@/api/apiClient';
import LoadingState from '@/components/shared/LoadingState';
import ErrorState from '@/components/shared/ErrorState';
import EmptyState from '@/components/shared/EmptyState';
import { useOdooAuth } from '@/lib/OdooAuthContext';

const fmt2 = (n) =>
  Number(n || 0).toLocaleString('ar-EG', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const STATE_LABEL = { draft: 'مسودة', submitted: 'بانتظار الاعتماد', approved: 'معتمد', rejected: 'مرفوض' };
const STATE_COLOR = {
  draft: 'bg-gray-100 text-gray-600 border-gray-200',
  submitted: 'bg-amber-50 text-amber-700 border-amber-200',
  approved: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  rejected: 'bg-red-50 text-red-700 border-red-200',
};

const TABS = [
  { key: 'submitted', label: 'قيد الاعتماد' },
  { key: 'all', label: 'الكل' },
  { key: 'approved', label: 'معتمد' },
  { key: 'rejected', label: 'مرفوض' },
];

export default function Returns() {
  const navigate = useNavigate();
  const { odooSession } = useOdooAuth();
  const isLeader = odooSession?.role === 'manager' || odooSession?.role === 'supervisor';

  // Leaders land on the approval queue; reps land on "all".
  const [tab, setTab] = useState(isLeader ? 'submitted' : 'all');
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    setLoading(true); setError(null);
    try {
      const res = await api.get('/sellgo_v5_returns', {
        state: tab === 'all' ? undefined : tab,
        limit: 100,
      });
      if (res?.success) setItems(Array.isArray(res.data) ? res.data : []);
      else setError(res?.error || 'تعذّر تحميل المرتجعات');
    } catch (e) {
      setError(e?.message || 'تعذّر تحميل المرتجعات');
    }
    setLoading(false);
  }, [tab]);

  useEffect(() => { load(); }, [load]);

  return (
    <div className="max-w-2xl mx-auto pb-8" dir="rtl">
      {/* Header */}
      <div className="px-4 pt-4 flex items-center justify-between">
        <div className="flex items-center gap-2 text-primary">
          <RotateCcw className="w-5 h-5" />
          <h1 className="text-lg font-bold">المرتجعات</h1>
        </div>
        <button onClick={load} className="p-2 rounded-lg hover:bg-muted text-muted-foreground">
          <RefreshCw className="w-4 h-4" />
        </button>
      </div>

      {/* Tabs */}
      <div className="px-4 mt-3 flex gap-2 overflow-x-auto pb-1">
        {TABS.map((t) => (
          <button
            key={t.key}
            onClick={() => setTab(t.key)}
            className={`px-3 py-1.5 rounded-full text-xs font-semibold whitespace-nowrap transition-colors border ${
              tab === t.key
                ? 'bg-primary text-white border-primary'
                : 'bg-card text-muted-foreground border-border hover:bg-muted'
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {/* List */}
      <div className="px-4 mt-3 space-y-2">
        {loading && <LoadingState message="جاري تحميل المرتجعات..." />}
        {!loading && error && <ErrorState message={error} onRetry={load} />}
        {!loading && !error && items.length === 0 && (
          <EmptyState
            icon={RotateCcw}
            title="لا توجد مرتجعات"
            description={tab === 'submitted' ? 'لا يوجد مرتجعات بانتظار الاعتماد.' : 'لم يتم إنشاء أي مرتجعات بعد.'}
          />
        )}
        {!loading && !error && items.map((r) => (
          <motion.button
            key={r.id}
            initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }}
            onClick={() => navigate(`/returns/${r.id}`)}
            className="w-full text-right bg-card border border-border rounded-2xl p-3 hover:border-primary/40 transition-colors flex items-center gap-3"
          >
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2">
                <span className="font-bold text-sm text-foreground">{r.name}</span>
                <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold border ${STATE_COLOR[r.state] || ''}`}>
                  {r.state_label || STATE_LABEL[r.state] || r.state}
                </span>
              </div>
              <p className="text-xs text-muted-foreground truncate mt-0.5">
                {Array.isArray(r.partner_id) ? r.partner_id[1] : '—'}
                {Array.isArray(r.sale_order_id) && <> • {r.sale_order_id[1]}</>}
              </p>
              {isLeader && Array.isArray(r.rep_user_id) && (
                <p className="text-[11px] text-muted-foreground/80 mt-0.5">المندوب: {r.rep_user_id[1]}</p>
              )}
              {r.create_date && (
                <p className="text-[11px] text-muted-foreground/80 mt-0.5">
                  {new Date(r.create_date.replace(' ', 'T')).toLocaleString('ar-EG', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
                </p>
              )}
            </div>
            <div className="flex flex-col items-end flex-shrink-0">
              <span className="font-bold text-sm text-foreground">{fmt2(r.amount_total)}</span>
              <ChevronLeft className="w-4 h-4 text-muted-foreground mt-1" />
            </div>
          </motion.button>
        ))}
      </div>
    </div>
  );
}
